import { Injectable } from '@angular/core';
import {
  HicapsConnectMerchant,
  HicapsConnectTerminal,
  Practice,
} from '@principle-theorem/principle-core';
import {
  IHicapsConnectMerchant,
  IHicapsConnectTerminal,
  IPractice,
} from '@principle-theorem/principle-core/interfaces';
import {
  Firestore,
  WithRef,
  asyncForEach,
  undeletedFilter,
} from '@principle-theorem/shared';
import { groupBy, partition } from 'lodash';

type MerchantTerminalGroup = [string, WithRef<IHicapsConnectTerminal>[]];

@Injectable({ providedIn: 'root' })
export class HicapsConnectV2MerchantSeedService {
  /** Firestore rejects empty ids and ids containing `. / [ ] *`. */
  private static _filterSeedableGroups(
    groups: MerchantTerminalGroup[],
    practice: WithRef<IPractice>
  ): MerchantTerminalGroup[] {
    const [seedable, skipped] = partition(groups, ([merchantId]) =>
      HicapsConnectTerminal.isValidKey(merchantId)
    );

    skipped.forEach(([merchantId]) => {
      // eslint-disable-next-line no-console
      console.error(
        `Unable to seed HICAPS Connect merchant "${merchantId}" for practice ${practice.name} (${practice.ref.id}) - merchantId is not a valid Firestore document ID`
      );
    });

    return seedable;
  }

  async seedMerchantsFromV1(practice: WithRef<IPractice>): Promise<void> {
    const v1Terminals = await this._loadUndeletedV1Terminals(practice);
    const existingMerchantIds = await this._loadExistingMerchantIds(practice);
    const merchantCol = Practice.hicapsConnectMerchantCol(practice);

    const terminalsByMerchant = groupBy(
      v1Terminals,
      (terminal) => terminal.merchantId
    );
    const seedableGroups =
      HicapsConnectV2MerchantSeedService._filterSeedableGroups(
        Object.entries(terminalsByMerchant),
        practice
      );

    await asyncForEach(seedableGroups, async ([merchantId, terminals]) => {
      if (existingMerchantIds.has(merchantId)) {
        return;
      }

      const practitionerRef =
        HicapsConnectMerchant.resolvePractitionerRef(terminals);
      const providerName = HicapsConnectMerchant.resolveProviderNameFromV1(
        merchantId,
        terminals
      );

      const newMerchant: IHicapsConnectMerchant = {
        merchantId,
        providerName,
        deleted: false,
        ...(practitionerRef ? { practitionerRef } : {}),
      };

      // A merchant doc created between the read above and this write must not be
      // clobbered; `addDoc` at a known id is a non-merging `setDoc`.
      await Firestore.upsertDoc(merchantCol, newMerchant, merchantId);
    });
  }

  private async _loadUndeletedV1Terminals(
    practice: WithRef<IPractice>
  ): Promise<WithRef<IHicapsConnectTerminal>[]> {
    const allTerminals = await Firestore.getDocs(
      Practice.hicapsConnectTerminalCol(practice)
    );
    return allTerminals.filter(undeletedFilter);
  }

  private async _loadExistingMerchantIds(
    practice: WithRef<IPractice>
  ): Promise<Set<string>> {
    const allMerchants = await Firestore.getDocs(
      Practice.hicapsConnectMerchantCol(practice)
    );
    return new Set(allMerchants.map((merchant) => merchant.merchantId));
  }
}
